import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TeamCardHeader from './TeamCardHeader';
import './TeamCard.scss';
import { getRankSuffix } from "../../../utils/GetRankSuffix";
import useNavigateToTop from '../../../hooks/general/useNavigateToTop';

const TeamCard = ({ 
    team, 
    intervalsToShow, 
    events,
    masterPoolId,
    gameId,
    gameAddress,
    findTournamentId,
    findTournamentInfo, 
}) => {
    const [hasOverflow, setHasOverflow] = useState(false);
    const scrollContainerRef = useRef(null);

    // Check if the scroll container has overflow
    useEffect(() => {
        const checkForOverflow = () => {
            if (scrollContainerRef.current) {
                const hasHorizontalOverflow = scrollContainerRef.current.scrollWidth > scrollContainerRef.current.clientWidth;
                setHasOverflow(hasHorizontalOverflow);
            }
        };

        // Check initially
        checkForOverflow();
        
        // Check on window resize
        window.addEventListener('resize', checkForOverflow);
        
        // Cleanup
        return () => {
            window.removeEventListener('resize', checkForOverflow);
        };
    }, [intervalsToShow]); // Re-run when intervals change

    const navigateToTop = useNavigateToTop();

    const finishes = intervalsToShow.map((interval) => {
        const tournamentId = findTournamentId(events, interval);
        const tournamentInfo = findTournamentInfo(team, tournamentId);
        
        if (!tournamentInfo) {
            // Return TBD directly if no tournament info is found
            return {
                event: `Event ${interval}`,
                value: "TBD",
            };
        }
        
        // Format the score for display
        const formattedScore = tournamentInfo.score === 0 ? "EVEN" : 
                              tournamentInfo.score > 0 ? `+${tournamentInfo.score}` : 
                              tournamentInfo.score;
                              
        // Build the value string with rank and score
        return {
            event: `Event ${interval}`,
            value: `${tournamentInfo.tied ? 'T-' : ''}${getRankSuffix(tournamentInfo.rank)} : (${formattedScore})`,
        };
    });

    const teamOverallRank = team?.scores?.overall?.rank;
    const teamOverallScore = team?.scores?.overall?.score;
    const teamOverallTied = team?.scores?.overall?.tied || false;
    //console.log("Team Tied", teamOverallTied, team?.scores?.overall);
    const formattedScore = teamOverallScore === 0 ? "E" : 
                          teamOverallScore > 0 ? `+${teamOverallScore}` : 
                          teamOverallScore;
    const formattedRank = getRankSuffix(teamOverallRank);

    const teamRank = `${teamOverallTied ? 'T-' : ''}${formattedRank} Place: (${formattedScore})`;
    const teamName = team?.teamName;
    const teamLogo = team?.teamLogo;

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
      }

    const hanleTeamPageClick = () => {
        // Handle the click event to navigate to the team page
        //console.log("Team Page Clicked", team.teamId);
        navigateToTop(`/${uriEncoded(gameAddress)}?mId=${uriEncoded(masterPoolId)}&gId=${uriEncoded(gameId)}&page=roster&teamId=${uriEncoded(team.teamId)}`);
    }

    return (
        <Box className="golf-league-team-card">
        <Box className="golf-league-team-card-container">
            {/* Header */}
            <TeamCardHeader 
                teamName={teamName}
                teamRank={teamRank}
                logoUrl={teamLogo}
            />

            {/* Scrollable Events Section */}
            <Box className="golf-league-events-container">
            <Box 
                ref={scrollContainerRef}
                className={`golf-league-events-scroll ${hasOverflow ? 'overflow' : ''}`}
            >
                {finishes?.map((finish, index) => (
                <Box key={index} className="golf-league-event-item">
                    <Typography variant="subtitle2" className="golf-league-event-title">
                    {finish.event}
                    </Typography>
                    <Typography variant="body2" className="golf-league-event-value">
                    {finish.value}
                    </Typography>
                </Box>
                ))}
            </Box>
            </Box>

            {/* Footer Button */}
            <Box className="golf-league-card-footer">
            <Button
                className="golf-league-roster-button"
                variant="outlined"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={hanleTeamPageClick}
            >
                Team Page
            </Button>
            </Box>
        </Box>
        </Box>
    );
};

export default TeamCard;